import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Plus,
  User,
  Clock,
  Loader2,
  MessageSquare,
  Edit2,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
interface NotesTabProps {
  notes: any[];
  newNote: string;
  setNewNote: (note: string) => void;
  onAddNote: () => void;
  readOnly?: boolean;
  isAddingNote?: boolean;
  currentUserId?: number | null;
  currentUserRoleId?: number | null;
  onUpdateNote?: (noteId: number, content: string) => Promise<boolean>;
  onDeleteNote?: (noteId: number) => Promise<boolean>;
}

export function NotesTab({
  notes,
  newNote,
  setNewNote,
  onAddNote,
  readOnly = false,
  isAddingNote = false,
  currentUserId = null,
  currentUserRoleId = null,
  onUpdateNote,
  onDeleteNote,
}: NotesTabProps) {
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Per product spec: edit and delete are both owner-only — even super
  // admins cannot modify another staff member's notes, since notes feed
  // the booking_activities audit trail.
  const canEditNote = (note: any) => {
    if (readOnly || !onUpdateNote) return false;
    return currentUserId != null && note.createdBy === currentUserId;
  };

  const canDeleteNote = (note: any) => {
    if (readOnly || !onDeleteNote) return false;
    return currentUserId != null && note.createdBy === currentUserId;
  };

  const confirmDelete = async () => {
    if (!onDeleteNote || pendingDeleteId == null) return;
    setDeleting(true);
    const ok = await onDeleteNote(pendingDeleteId);
    setDeleting(false);
    if (ok) setPendingDeleteId(null);
  };

  const startEdit = (note: any) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content || "");
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent("");
  };

  const saveEdit = async (noteId: number) => {
    if (!onUpdateNote || !editingContent.trim()) return;
    setSavingEdit(true);
    const ok = await onUpdateNote(noteId, editingContent.trim());
    setSavingEdit(false);
    if (ok) cancelEdit();
  };
  return (
    <div className="bg-white rounded-lg border h-full flex flex-col">
      <div className="p-4 space-y-4 flex flex-col h-full">
        {/* Add Note */}
        {!readOnly && (
          <div className="space-y-2 border-b pb-4">
            <Textarea
              placeholder="Add an internal note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="resize-none"
              rows={3}
              disabled={isAddingNote}
            />
            <Button
              onClick={onAddNote}
              disabled={!newNote.trim() || isAddingNote}
              size="sm"
            >
              {isAddingNote ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add internal note
                </>
              )}
            </Button>
          </div>
        )}

        {/* Notes List */}
        <ScrollArea className="flex-1">
          <div className="space-y-4 pr-4">
            {notes.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No internal notes yet</p>
            ) : (
              notes.map((note, index) => {
                const isEditing = editingNoteId === note.id;
                return (
                  <div key={note.id} className="flex gap-4">
                    {/* Timeline Line */}
                    <div className="relative flex flex-col items-center">
                      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <MessageSquare className="h-4 w-4 text-blue-500" />
                      </div>
                      {index < notes.length - 1 && (
                        <div
                          className="flex-1 w-px bg-gray-200 mt-2"
                          style={{ minHeight: "24px" }}
                        ></div>
                      )}
                    </div>

                    {/* Note Content */}
                    <div className="flex-1 pb-4">
                      {isEditing ? (
                        <div className="space-y-2 mb-2">
                          <Textarea
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            rows={3}
                            className="resize-none"
                            disabled={savingEdit}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveEdit(note.id)}
                              disabled={!editingContent.trim() || savingEdit}
                            >
                              {savingEdit ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                "Save"
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEdit}
                              disabled={savingEdit}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap flex-1">
                            {note.content}
                          </p>
                          <div className="flex items-center ml-2">
                            {canEditNote(note) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => startEdit(note)}
                                aria-label="Edit note"
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                            )}
                            {canDeleteNote(note) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setPendingDeleteId(note.id)}
                                aria-label="Delete note"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <User className="h-3 w-3" />
                          <span>{note.author}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="h-3 w-3" />
                          <span>{new Date(note.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setPendingDeleteId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the note from the booking. This action can&apos;t
              be undone from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
